import { useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — sign in with your new one");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="New password"
      title="Choose a new one."
      subtitle={missingToken || done ? null : "Pick something at least 6 characters. You'll use it to sign in from now on."}
    >
      {missingToken ? (
        <div data-testid="reset-password-invalid" className="space-y-4">
          <p style={{ color: "var(--terracotta)" }}>
            This reset link is missing a token. Request a new one below.
          </p>
          <Link to="/forgot-password" data-testid="reset-password-request-new" className="underline text-sm">
            Send me a new link
          </Link>
        </div>
      ) : done ? (
        <div data-testid="reset-password-done" className="space-y-4">
          <p style={{ color: "var(--ink-soft)" }}>
            Password updated. Redirecting you to sign in…
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest">New password</Label>
            <Input
              id="password"
              data-testid="reset-password-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoFocus
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs uppercase tracking-widest">Confirm password</Label>
            <Input
              id="confirm"
              data-testid="reset-password-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="mt-2"
            />
          </div>
          {error && (
            <div data-testid="reset-password-error" className="text-sm" style={{ color: "var(--terracotta)" }}>
              {error}
            </div>
          )}
          <Button
            type="submit"
            data-testid="reset-password-submit"
            disabled={busy}
            className="btn-primary w-full py-6 rounded-full"
          >
            {busy ? "Saving…" : "Update password"}
          </Button>
          <div className="text-sm text-center" style={{ color: "var(--ink-soft)" }}>
            <Link to="/login" data-testid="reset-password-to-login" className="underline">
              Back to sign in
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
